/* eslint-disable camelcase */
import React, { useState } from "react";
import { Dropdown, DropdownButton, Form, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleUp,
  faArrowUpRightFromSquare,
  faEnvelope,
  faMessage,
  faPen,
  faPhone,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useGetBoomLogicWFMessagingDataQuery } from "../store/slices/widgetsSlice.ts";
import MyPagination from "../Common/MyPagination";
import SelectInput from "../Builder/nodes/Logic/Inputs/SelectInput";
import { useGetProjectsQuery } from "../store/slices/projectsAPISlice.ts";
import RepliesModal from "../Reporting/RealtimeReporting/RepliesModal";

const columnOptions = [
  // { value: "Started", label: "Started" },
  { value: "Process", label: "Process" },
  { value: "Component", label: "Component" },
  { value: "Sent", label: "Sent" },
  { value: "Delivered", label: "Delivered" },
  { value: "Failed", label: "Failed" },
  { value: "Replies", label: "Replies" },
];

const channelDetails = {
  sms: { icon: faMessage, label: "SMS" },
  email: { icon: faEnvelope, label: "email" },
  voice: { icon: faPhone, label: "voice" },
};

function transactionLink(item, channel, messageStatus) {
  const params = new URLSearchParams({
    projectUUID: item.project_uuid,
    startDate: item.process_started_at,
    comms: channel,
  });
  if (messageStatus) {
    params.set("messageStatus", messageStatus);
  }
  return `/reporting/realtime?${params.toString()}`;
}

function ChannelMetric({ item, metric, messageStatus, onShowReplies }) {
  const channels = item.channels;
  if (!channels) {
    return item[metric] || "—";
  }

  const entries = Object.entries(channels).filter(([, stats]) => stats[metric] > 0);
  if (!entries.length) {
    return "—";
  }

  return entries.map(([channel, stats]) => {
    const { icon, label } = channelDetails[channel];
    const count = stats[metric];
    const description = `${count} ${label} ${metric}`;
    const content = (
      <>
        <FontAwesomeIcon icon={icon} aria-hidden /> <span>{count}</span>
      </>
    );

    return metric === "replies" ? (
      <button
        className="btn btn-link p-0 me-2"
        key={channel}
        type="button"
        onClick={() => onShowReplies(channel)}
        title={`View ${description}`}
        aria-label={`View ${description}`}
      >
        {content}
      </button>
    ) : (
      <Link
        className="me-2"
        key={channel}
        to={transactionLink(item, channel, messageStatus)}
        title={`View ${description} transactions`}
        aria-label={`View ${description} transactions`}
      >
        {content}
      </Link>
    );
  });
}

export default function BoomLogicWFMessaging({ showTransactions = true }) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showReplies, setShowReplies] = useState(null);
  const [filters, setFilters] = useState({
    project_uuid: [],
    process_guid: "",
    date_gte: "",
    date_lte: "",
  });
  const [order, setOrder] = useState("desc");
  const [order_by, setOrder_by] = useState("start_at");
  const [page, setPage] = useState(0);
  const [per_page, setPerPage] = useState(10);
  const [columnsToShow, setColumnsToShow] = useState(
    localStorage.getItem("WFMessagingOverviewColumns")
      ? JSON.parse(localStorage.getItem("WFMessagingOverviewColumns"))
      : ["report", "Process", "Started", "Component", "Sent", "Delivered"]
  );

  const { data: projectsData = { projects: [] } } = useGetProjectsQuery({ limit: 100, offset: 0 });
  const componentOptions = (projectsData.projects || []).map((project) => ({
    value: project.UUID || project.Uuid || project.uuid,
    label: project.Label || project.label,
    details: [project.Reference || project.reference, project.Description || project.description]
      .filter(Boolean)
      .join(", "),
  })).filter((project) => project.value);
  const { data = { components: [], total: 0 }, refetch } =
    useGetBoomLogicWFMessagingDataQuery({
      order,
      order_by,
      page,
      per_page,
      ...filters,
    });

  const updateFilters = (changes) => {
    setFilters((previous) => ({ ...previous, ...changes }));
    setPage(0);
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-3 gap-3 flex-wrap">
        <button type="button" className="btn btn-colored" aria-expanded={showAdvancedFilters} onClick={() => setShowAdvancedFilters((previous) => !previous)}>
          Filters
        </button>
        <DropdownButton
          id="show-colums-dropdown"
          title="Show columns"
          variant="colored"
          autoClose="outside"
          align="end"
          className="h-100 rounded-3"
        >
          {columnOptions.map((c) => (
            <Dropdown.Item as="div" key={c.value}>
              <Form.Check
                checked={columnsToShow.includes(c.value)}
                label={c.label}
                id={`${c.value}-checkbox`}
                onChange={() => {
                  setColumnsToShow((p) => {
                    let newColumns;
                    if (p.includes(c.value)) {
                      newColumns = p.filter((pc) => pc !== c.value);
                    } else {
                      newColumns = [...p, c.value];
                    }
                    localStorage.setItem(
                      "WFMessagingOverviewColumns",
                      JSON.stringify(newColumns)
                    );
                    return newColumns;
                  });
                }}
              />
            </Dropdown.Item>
          ))}
        </DropdownButton>
        <button
          type="button"
          className="btn btn-sm border-0"
          onClick={refetch}
          title="Refresh"
        >
          <FontAwesomeIcon icon={faRotateLeft} />
        </button>
      </div>
      {showAdvancedFilters && (
        <div className="asset-filter-panel mb-3 bg-colored-reverse p-3 m-0 rounded-3 text-white">
          <div className="d-flex flex-column gap-3">
            <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
              <div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>Components</div>
              <div style={{ minWidth: 260, flex: "1 1 420px" }}><SelectInput value={filters.project_uuid} onChange={(project_uuid) => updateFilters({ project_uuid })} options={componentOptions} isMulti isClearable placeholder="Select components" /></div>
            </div>
            <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
              <div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>Sent</div>
              <div className="d-flex flex-wrap gap-3"><Form.Group style={{ width: 250 }}><Form.Label className="small mb-1">From</Form.Label><Form.Control aria-label="Sent from" type="datetime-local" value={filters.date_gte} onChange={(event) => updateFilters({ date_gte: event.target.value })} /></Form.Group><Form.Group style={{ width: 250 }}><Form.Label className="small mb-1">To</Form.Label><Form.Control aria-label="Sent to" type="datetime-local" value={filters.date_lte} onChange={(event) => updateFilters({ date_lte: event.target.value })} /></Form.Group></div>
            </div>
            <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
              <div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>Process GUID</div>
              <Form.Control placeholder="Exact process GUID" value={filters.process_guid} onChange={(event) => updateFilters({ process_guid: event.target.value })} style={{ minWidth: 180, flex: "1 1 420px" }} />
            </div>
            <div><button type="button" className="btn btn-sm btn-outline-light" onClick={() => updateFilters({ project_uuid: [], process_guid: "", date_gte: "", date_lte: "" })}>Clear filters</button></div>
          </div>
        </div>
      )}
      <div>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>
                <button
                  className="btn btn-link p-0"
                  type="button"
                  onClick={() => {
                    if (order_by !== "start_at") {
                      setOrder_by("start_at");
                      setOrder("desc");
                    } else {
                      setOrder((p) => (p === "asc" ? "desc" : "asc"));
                    }
                  }}
                >
                  Started
                  {order_by === "start_at" && (
                    <FontAwesomeIcon
                      className="ms-2"
                      icon={order === "asc" ? faAngleUp : faAngleDown}
                    />
                  )}
                </button>
              </th>

              {columnsToShow.includes("Process") && <th>Process</th>}
              {columnsToShow.includes("Component") && <th>Component</th>}
              {columnsToShow.includes("Sent") && (
                <th>
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    onClick={() => {
                      if (order_by !== "dispatched") {
                        setOrder_by("dispatched");
                        setOrder("desc");
                      } else {
                        setOrder((p) => (p === "asc" ? "desc" : "asc"));
                      }
                    }}
                  >
                    Sent
                    {order_by === "dispatched" && (
                      <FontAwesomeIcon
                        className="ms-2"
                        icon={order === "asc" ? faAngleUp : faAngleDown}
                      />
                    )}
                  </button>
                </th>
              )}
              {columnsToShow.includes("Delivered") && (
                <th>
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    onClick={() => {
                      if (order_by !== "delivered") {
                        setOrder_by("delivered");
                        setOrder("desc");
                      } else {
                        setOrder((p) => (p === "asc" ? "desc" : "asc"));
                      }
                    }}
                  >
                    Delivered
                    {order_by === "delivered" && (
                      <FontAwesomeIcon
                        className="ms-2"
                        icon={order === "asc" ? faAngleUp : faAngleDown}
                      />
                    )}
                  </button>
                </th>
              )}
              {columnsToShow.includes("Failed") && (
                <th>
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    onClick={() => {
                      if (order_by !== "failed") {
                        setOrder_by("failed");
                        setOrder("desc");
                      } else {
                        setOrder((p) => (p === "asc" ? "desc" : "asc"));
                      }
                    }}
                  >
                    Failed
                    {order_by === "failed" && (
                      <FontAwesomeIcon
                        className="ms-2"
                        icon={order === "asc" ? faAngleUp : faAngleDown}
                      />
                    )}
                  </button>
                </th>
              )}
              {columnsToShow.includes("Replies") && (
                <th>
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    onClick={() => {
                      if (order_by !== "replies") {
                        setOrder_by("replies");
                        setOrder("desc");
                      } else {
                        setOrder((p) => (p === "asc" ? "desc" : "asc"));
                      }
                    }}
                  >
                    Replies
                    {order_by === "replies" && (
                      <FontAwesomeIcon
                        className="ms-2"
                        icon={order === "asc" ? faAngleUp : faAngleDown}
                      />
                    )}
                  </button>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.components.map((item, i) => (
              <tr key={i}>
                <td>
                  {new Date(item.process_started_at).toLocaleString()}{" "}
                  {showTransactions && item.channels &&
                    Object.entries(item.channels)
                      .filter(([, stats]) =>
                        ["dispatched", "delivered", "failed", "replies"].some(
                          (metric) => stats[metric] > 0
                        )
                      )
                      .map(([channel]) => {
                        const { icon, label } = channelDetails[channel];
                        return (
                          <Link
                            to={transactionLink(item, channel)}
                            className="ms-2"
                            key={channel}
                            title={`View ${label} transactions`}
                            aria-label={`View ${label} transactions`}
                          >
                            <FontAwesomeIcon icon={icon} />
                          </Link>
                        );
                      })}
                  {showTransactions && !item.channels && (
                    <Link
                      to={`/reporting/realtime/transactions?${new URLSearchParams({
                        projectUUID: item.project_uuid,
                        startDate: item.process_started_at,
                      }).toString()}`}
                      className="ms-2"
                      title="View transactions"
                      aria-label="View transactions"
                    >
                      <FontAwesomeIcon icon={faMessage} />
                    </Link>
                  )}
                </td>

                {columnsToShow.includes("Process") && (
                  <td>
                    {item.process_guid}
                    <Link
                      to={`/reporting/processes/${item.project_id}/process/${item.process_id}`}
                      title="Open process details"
                      className="mx-2"
                    >
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </Link>
                  </td>
                )}
                {columnsToShow.includes("Component") && (
                  <td>
                    {item.project_label}
                    <Link
                      to={`/library/logic/builder/${item.project_id}`}
                      className="mx-2"
                      title="Edit component"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </Link>
                  </td>
                )}
                {columnsToShow.includes("Sent") && (
                  <td>
                    <ChannelMetric item={item} metric="dispatched" messageStatus="sent" />
                  </td>
                )}
                {columnsToShow.includes("Delivered") && (
                  <td>
                    <ChannelMetric item={item} metric="delivered" messageStatus="delivered" />
                  </td>
                )}
                {columnsToShow.includes("Failed") && (
                  <td>
                    <ChannelMetric item={item} metric="failed" messageStatus="failed" />
                  </td>
                )}
                {columnsToShow.includes("Replies") && (
                  <td>
                    <ChannelMetric
                      item={item}
                      metric="replies"
                      onShowReplies={(channel) =>
                        setShowReplies({
                          channel,
                          context: {
                            kind: "process",
                            value: item.process_guid,
                            channel,
                          },
                        })
                      }
                    />
                  </td>
                )}
              </tr>
            ))}
            {!data.components.length && (
              <tr>
                <td
                  className="text-center text-secondary"
                  colSpan={
                    1 +
                    columnOptions.filter((c) => columnsToShow.includes(c.value))
                      .length
                  }
                >
                  No component messaging found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <RepliesModal showReplies={showReplies} setShowReplies={setShowReplies} />
      <MyPagination
        total={data.total}
        onPaginate={(p, pp) => {
          setPage(p);
          setPerPage(pp);
        }}
        perPageOptions={[10, 25, 50]}
        defaultPerPage={10}
        alwaysVisible
      />
    </>
  );
}
